import { useRef } from 'react';
import { Loader2, Upload } from 'lucide-react';
import FileDropZone from '../../../components/common/FileDropZone';
import { Button } from '../../../components/ui/button';
import type { SupplierUploadTracking } from '../../../lib/api/barratt';
import { UPLOAD_ACCEPT, datasetLabel, periodLabelFor } from '../uploadCycle';

interface RowFileButtonProps {
  row: SupplierUploadTracking;
  uploading: boolean;
  disabled: boolean;
  onUpload: (file: File) => void;
  /**
   * `replace` for a month already in — a rare correction, so it stays quiet.
   * `upload` for a month with no file yet, where it is the way to send one.
   */
  mode?: 'replace' | 'upload';
  /** Where this button sits; a row can appear in both a card and the table. */
  context?: 'card' | 'table';
}

/**
 * The file picker on a single row of the record.
 *
 * The drop zone is what takes the file — hidden here purely for its picker and
 * its accept / size checks, so every upload in the app validates identically.
 * The card above gets the full drop zone; a table row gets this.
 */
export default function RowFileButton({
  row,
  uploading,
  disabled,
  onUpload,
  mode = 'replace',
  context = 'card',
}: RowFileButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const replacing = mode === 'replace';
  const what = `${datasetLabel(row)} file for ${periodLabelFor(row)}`;

  return (
    <>
      <Button
        variant={replacing ? 'ghost' : 'outline'}
        size="sm"
        className={replacing ? 'text-muted-foreground hover:text-foreground' : undefined}
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        aria-label={`${replacing ? 'Replace' : 'Upload'} ${what}`}
        data-testid={`${mode}-${context}-${row.dataset_type}-${row.id}`}
      >
        {uploading ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="mr-1.5 h-3.5 w-3.5" />
        )}
        {uploading ? 'Uploading…' : replacing ? 'Replace' : 'Upload'}
      </Button>
      <div className="hidden">
        <FileDropZone
          accept={UPLOAD_ACCEPT}
          busy={uploading}
          disabled={disabled}
          inputRef={inputRef}
          onFiles={(files) => onUpload(files[0])}
          data-testid={`${mode}-${context}-picker-${row.dataset_type}-${row.id}`}
        />
      </div>
    </>
  );
}
